import { getGlobal } from "../global";
import { PyScriptWorker, WorkerInfo, WorkerResponse, BUILD_VERSION } from "./worker";
import { PyEnvironmentStatus, PyWorkerConfig } from "./pytypes";
import { PyScriptInterpreter } from "./types";

const DEBUG = false;

const g = getGlobal();

export interface PyWorkerInfo extends WorkerInfo {
  worker: PyWorker;
  config: PyWorkerConfig | (() => Promise<PyWorkerConfig>);
}

export interface PyWorker extends PyScriptWorker {
  loadPyScriptApps: (pkgs: string[]) => Promise<string | null>;
  micropipInstall: (pkgs: string[]) => Promise<string | null>;
  stepGenerator: (genKey: string) => Promise<WorkerResponse>;
  stopGenerator: (genKey: string) => void;
}

let pyEnvStatusFn: (status: PyEnvironmentStatus) => void = null;

export async function getPyWorker(
  info: PyWorkerInfo,
  setPyEnvStatusFn?: (status: PyEnvironmentStatus) => void,
  customInit?: (interpreter: PyScriptInterpreter, worker: PyWorker) => Promise<void>
): Promise<PyWorker> {
  if (info.worker === undefined) {
    if (DEBUG) console.log("info.worker is undefined. Creating new Python worker");
    if (!info.loadingFlag) {
      // Grab the flag
      info.loadingFlag = true;
      // Wait for PyWorker to be available
      while (!g.PyWorker) {
        if (DEBUG) console.log("Waiting for PyWorker to be defined");
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      // Register pyEnvStatusFn
      pyEnvStatusFn = setPyEnvStatusFn ?? null;
    } else {
      // Register pyEnvStatusFn (if not already set)
      //   This is used for the case where getPyWorker is called by function.ts outside of React
      //   and while building the worker, the UI attempts to get the worker and needs callbacks
      if (pyEnvStatusFn === null && setPyEnvStatusFn) pyEnvStatusFn = setPyEnvStatusFn;
      // Wait for 0.5 seconds and try again
      if (DEBUG) console.log("Waiting for another thread to getPyWorker()");
      await new Promise((resolve) => setTimeout(resolve, 500));
      return await getPyWorker(info);
    }

    try {
      // Resolve any delayed settings
      if (typeof info.config === "function") {
        info.config = await info.config();
      }
      if (DEBUG) console.log(`info = ${JSON.stringify(info)}`);

      const rawWorker = await g.PyWorker(`/pyscript/static/pyworker/${BUILD_VERSION}/worker.py`, {
        version: info.config.pyodideVersion,
        config: {
          files: {
            [`/pyscript/static/pyworker/${BUILD_VERSION}/utils.py`]: "",
            [`/pyscript/static/pyworker/${BUILD_VERSION}/udf.py`]: "",
          },
        },
      });
      info.terminate = () => rawWorker.terminate();
      const py: PyWorker = rawWorker.sync;
      if (DEBUG) console.log("PyWorker created");

      // Pre-load packages
      await py.loadPackages(["setuptools"]);
      if (info.config.pyodidePackages?.length) {
        if (pyEnvStatusFn) pyEnvStatusFn(PyEnvironmentStatus.LOADING_PYODIDE_PACKAGES);
        const error = await py.loadPackages(info.config.pyodidePackages);
        if (error) throw new Error(error);
      }

      if (info.config.pyscriptApps?.length) {
        if (pyEnvStatusFn) pyEnvStatusFn(PyEnvironmentStatus.LOADING_PYSCRIPT_APPS);
        const error = await py.loadPyScriptApps(info.config.pyscriptApps);
        if (error) throw new Error(error);
      }

      if (info.config.micropipPackages?.length) {
        await py.loadPackages(["micropip"]);
        if (pyEnvStatusFn) pyEnvStatusFn(PyEnvironmentStatus.LOADING_MICROPIP_PACKAGES);
        await py.micropipInstall(info.config.micropipPackages);
        const error = await py.micropipInstall(info.config.micropipPackages);
        if (error) throw new Error(error);
      }

      // Initialize
      if (DEBUG) console.log("Calling py.initialize()");
      if (pyEnvStatusFn) pyEnvStatusFn(PyEnvironmentStatus.RUNNING_INIT_CODE);
      await py.initialize();

      if (customInit) {
        await customInit(PyScriptInterpreter.pyodide, py);
      } else {
        const resp = await py._runInit(info.config.initCode);
        if (resp.error) {
          throw new Error(resp.error);
        }
      }

      info.worker = py;
    } catch (err) {
      console.log("Error caught while creating new PyScript.PyWorker");
      console.error(err);
      throw err;
    } finally {
      pyEnvStatusFn = null;
    }
  }
  return info.worker;
}
